import React, { useState, useRef, useEffect } from 'react';
import { Search, User, X } from 'lucide-react';
import { Input } from '@/components/ui/input';

/**
 * Searchable client selector with autocomplete.
 * Filters by first_name, last_name, phone, email.
 * Shows max 15 results for performance.
 */
const ClientSearchSelect = ({ clients = [], value, onChange, placeholder = 'Rechercher un client...', testId = 'client-search' }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Set label when value changes externally
  useEffect(() => {
    if (value) {
      const c = clients.find(cl => cl.id === value);
      if (c) setSelectedLabel(`${c.first_name} ${c.last_name}`);
    } else {
      setSelectedLabel('');
    }
  }, [value, clients]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const normalize = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const filtered = query.length >= 1
    ? clients.filter(c => {
        const q = normalize(query);
        return normalize(c.first_name).includes(q) ||
               normalize(c.last_name).includes(q) ||
               normalize(`${c.first_name} ${c.last_name}`).includes(q) ||
               normalize(`${c.last_name} ${c.first_name}`).includes(q) ||
               (c.phone && c.phone.includes(query)) ||
               (c.email && normalize(c.email).includes(q));
      }).slice(0, 15)
    : [];

  const handleSelect = (client) => {
    onChange(client.id);
    setSelectedLabel(`${client.first_name} ${client.last_name}`);
    setQuery('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setSelectedLabel('');
    setQuery('');
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid={testId}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        {selectedLabel && !open ? (
          <div
            className="flex items-center h-9 w-full rounded-md border border-input bg-background px-8 py-1 text-sm cursor-pointer hover:bg-slate-50"
            onClick={() => { setOpen(true); setQuery(''); setTimeout(() => inputRef.current?.focus(), 50); }}
            data-testid={`${testId}-selected`}
          >
            <span className="flex-1 truncate font-medium text-slate-900">{selectedLabel}</span>
            <button onClick={(e) => { e.stopPropagation(); handleClear(); }} className="ml-1 text-slate-400 hover:text-red-500" data-testid={`${testId}-clear`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="pl-8 h-9"
            autoComplete="off"
            data-testid={`${testId}-input`}
          />
        )}
      </div>

      {open && query.length >= 1 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid={`${testId}-dropdown`}>
          {filtered.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-3">Aucun client trouve</p>
          ) : (
            filtered.map(c => (
              <button
                key={c.id}
                onClick={() => handleSelect(c)}
                className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2.5 transition-colors border-b border-slate-50 last:border-0"
                data-testid={`${testId}-option-${c.id}`}
              >
                <div className="w-7 h-7 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold shrink-0">
                  {(c.first_name?.[0] || '').toUpperCase()}{(c.last_name?.[0] || '').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{c.first_name} {c.last_name}</p>
                  <p className="text-[10px] text-slate-500 truncate">
                    {c.phone && <span>{c.phone}</span>}
                    {c.phone && c.email && <span> · </span>}
                    {c.email && <span>{c.email}</span>}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ClientSearchSelect;
